/**
 * The cycle the item page was opened from, and the item's standing there
 * (migration 1019, roadmap S-37).
 *
 * `?cycle=` names the cycle; `?plan=` names a plan, and so the cycle it was
 * built from. While the page shows a cycle still ranking the item, its writes
 * carry that cycle, so the server can check them against it and record the
 * version they make.
 */

import { useEffect, useState } from 'react';
import { setWorkingCycle } from '@/lib/itemCycleContext';
import { cycleInContext, cycleOpenedFrom, type ItemCycleStanding } from '@/lib/itemCycleStanding';
import { fetchItemCycleStanding } from '@/lib/itemCycleStandingService';

export function useItemCycleStanding(itemId: string, cycleParam: string | null, planParam: string | null, reloadKey: string): ItemCycleStanding | null {
  const [standing, setStanding] = useState<ItemCycleStanding | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      let read = await fetchItemCycleStanding(itemId, cycleParam).catch(() => null);
      const opened = cycleOpenedFrom({ cycleParam, planParam, plans: read?.plans ?? [] });
      if (opened && opened !== cycleParam) read = await fetchItemCycleStanding(itemId, opened).catch(() => null);
      if (live) setStanding(read);
    })();
    return () => {
      live = false;
    };
  }, [itemId, cycleParam, planParam, reloadKey]);

  const cycle = cycleInContext(standing);
  const rankingCycleId = cycle?.mode === 'ranking' ? cycle.id : null;
  useEffect(() => {
    setWorkingCycle(rankingCycleId);
    return () => setWorkingCycle(null);
  }, [rankingCycleId]);

  return standing;
}
